/**
 * The language a page asks for the speech in (SEO_BRIEF_09 sections 2.1 and 5), and the two
 * usage events a translation makes (section 2.7).
 *
 * A page whose row has `translate_to` renders the target picker beside its drop zone or link box
 * (tools/_translate_picker.html, `[data-translate-target]`) with the row's target chosen. The
 * upload (lib/upload-offer.ts) and the link (pages/landing.ts) send its value as `translate_to`
 * (contracts/api.md 1.18.0). A page with the row's target in its config and no picker sends that.
 * Every other page sends nothing, and its requests are byte for byte what they were.
 *
 * `translate_start {target, engine, source, trigger}` is counted when the server takes the
 * request, with the way it expects to translate (the answer's `translate.engine`) and `trigger:
 * 'picker'`: on a tool page the target comes from its picker or its own config, never from the
 * transcript page's "Translate to English" button (`en_button`). `translate_done {target,
 * engine}` is counted when the translation is ready: at once for one served from the cache, on
 * the job page when the job finishes (pages/job.ts). The job page counts it under the page that
 * started the job, kept for it in this tab's session storage; without that (another tab), under
 * `transcript`. Nothing is counted for a request that will not be translated (`engine` null).
 */

import type { TranslateMember } from './api-types.ts';
import { qs } from './dom.ts';
import {
  countUse,
  type TranslateSource,
  type TranslateTrigger,
  TRANSLATE_ENGINES,
  type UsageFormat,
} from './usage.ts';

/** The picker's select. */
export const PICKER = '[data-translate-target]';
/** A target as the server takes it: a Whisper code or zh-Hant (server/pipeline/translate.py). */
export const TARGET = /^(?:[a-z]{2,3}|zh-Hant)$/;
/** Where a started translation waits for its job to finish, per job id. */
const STARTED = 'tw-translate:';
/** The job page's tool when the page that started the job is not known. */
const FALLBACK_TOOL = 'transcript';
/** The transcript page's query naming the language this visitor's submission asked for. */
export const ASKED_PARAM = 'asked';
const RELATIVE = 'http://relative.invalid';

/**
 * `url` with `?asked={target}` when the submission asked for a translation (the `translate` member
 * of its answer, or of the job's poll), else `url` untouched. The transcript page says "This is
 * already in Spanish, so here is the transcript." when the transcript is already in the language
 * asked for (routes_pages.translation_context checks it against the document). It rides on this
 * visitor's address only: nothing is stored on the transcript every later visitor shares.
 */
export function withAsked(url: string, translate: TranslateMember | null | undefined): string {
  const target = translate?.target;
  if (typeof target !== 'string' || !TARGET.test(target)) return url;
  const parsed = new URL(url, RELATIVE);
  parsed.searchParams.set(ASKED_PARAM, target);
  return parsed.origin === RELATIVE
    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
    : parsed.href;
}

/** The page's own target from its config block (tools/tool.html `data-tool-config`). */
function configTarget(root: ParentNode): string | undefined {
  const block = qs<HTMLScriptElement>('script[data-tool-config]', root);
  if (!block) return undefined;
  try {
    const config = JSON.parse(block.textContent ?? 'null') as { translate_to?: unknown } | null;
    const value = config?.translate_to;
    return typeof value === 'string' && TARGET.test(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The target to send: the picker's choice, else the page's own, else undefined (a page that does
 * not translate).
 */
export function translateTarget(root: ParentNode = document): string | undefined {
  const picker = qs<HTMLSelectElement>(PICKER, root);
  const picked = picker?.value;
  if (typeof picked === 'string' && TARGET.test(picked)) return picked;
  return configTarget(root);
}

/**
 * The picked target's name as a sentence says it, in the page's language, for the upload offer's
 * "we'll translate it into {language}" (base/macros/states.html B6, SEO_BRIEF_09 Part C): the
 * option's `data-name` where its label reads differently in a sentence ("Inglés" in the list,
 * "inglés" in the sentence), else its label. Undefined without a picker, and the offer keeps the
 * page's own target, which the server wrote into it.
 */
export function translateTargetName(root: ParentNode = document): string | undefined {
  const picker = qs<HTMLSelectElement>(PICKER, root);
  const option = picker?.selectedOptions?.[0];
  if (!option || !TARGET.test(option.value)) return undefined;
  const name = (option.getAttribute('data-name') ?? option.textContent ?? '').trim();
  return name === '' ? undefined : name;
}

function isEngine(value: unknown): value is (typeof TRANSLATE_ENGINES)[number] {
  return typeof value === 'string' && (TRANSLATE_ENGINES as readonly string[]).includes(value);
}

function remember(jobId: string, tool: string, format: UsageFormat): void {
  try {
    sessionStorage.setItem(STARTED + jobId, JSON.stringify({ tool, format }));
  } catch {
    /* storage refused: the job page counts it under `transcript` */
  }
}

function recall(jobId: string): { tool: string; format: UsageFormat } | null {
  try {
    const raw = sessionStorage.getItem(STARTED + jobId);
    if (raw === null) return null;
    sessionStorage.removeItem(STARTED + jobId);
    const parsed = JSON.parse(raw) as { tool?: unknown; format?: unknown };
    if (typeof parsed.tool !== 'string' || typeof parsed.format !== 'string') return null;
    return { tool: parsed.tool, format: parsed.format as UsageFormat };
  } catch {
    return null;
  }
}

/**
 * The server took a request to translate: `translate_start`, and for one served at once from the
 * cache `translate_done` too. `jobId` is the queued job's, for its page to count the done.
 * `trigger` is `picker` but for the transcript page's speech-recognition fallback after its
 * "Translate to English" button (pages/transcript-actions.ts).
 */
export function countTranslateStart(
  answer: TranslateMember | undefined,
  tool: string,
  format: UsageFormat,
  source: TranslateSource,
  jobId?: string,
  trigger: TranslateTrigger = 'picker',
): void {
  if (!answer || !isEngine(answer.engine)) return;
  const { target, engine } = answer;
  countUse('translate_start', tool, format, { target, engine, source, trigger });
  if (jobId) remember(jobId, tool, format);
  else countUse('translate_done', tool, format, { target, engine });
}

/** A translation job finished (pages/job.ts): `translate_done`, how the poll says it was made. */
export function countTranslateDone(jobId: string, answer: TranslateMember | undefined): void {
  const started = recall(jobId);
  if (!answer || !isEngine(answer.engine)) return;
  countUse('translate_done', started?.tool ?? FALLBACK_TOOL, started?.format ?? 'unknown', {
    target: answer.target,
    engine: answer.engine,
  });
}
